import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import { FiEye, FiEyeOff, FiUpload } from "react-icons/fi";
import "../styles/LoginPage.css";
import { useTranslation } from "react-i18next";

import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

const PDFJS_VERSION = pdfjs?.version || "5.4.296";
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${PDFJS_VERSION}/build/pdf.worker.min.mjs`;

const REGISTER_DRAFT_KEY = "register_form_draft";
const REGISTER_CARD_REF_KEY = "register_card_payment_ref";
const REGISTER_PAYMENT_STATE_KEY = "register_payment_state";

function saveRegisterDraft(draft) {
  localStorage.setItem(REGISTER_DRAFT_KEY, JSON.stringify(draft));
}

function loadRegisterDraft() {
  try {
    return JSON.parse(localStorage.getItem(REGISTER_DRAFT_KEY) || "{}");
  } catch {
    return {};
  }
}

function clearRegisterDraft() {
  localStorage.removeItem(REGISTER_DRAFT_KEY);
}

function saveRegisterPaymentState(state) {
  localStorage.setItem(REGISTER_PAYMENT_STATE_KEY, JSON.stringify(state));
}

function loadRegisterPaymentState() {
  try {
    return JSON.parse(localStorage.getItem(REGISTER_PAYMENT_STATE_KEY) || "{}");
  } catch {
    return {};
  }
}

function clearRegisterPaymentState() {
  localStorage.removeItem(REGISTER_PAYMENT_STATE_KEY);
  localStorage.removeItem(REGISTER_CARD_REF_KEY);
}

export default function RegisterPage() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const location = useLocation();

  const [openPdf, setOpenPdf] = useState(null);

  const [opened, setOpened] = useState({
    kvkk: false,
    riza: false,
    terms: false,
    dpa: false,
  });

  const [fullName, setFullName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("doctor");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [showPwd1, setShowPwd1] = useState(false);
  const [showPwd2, setShowPwd2] = useState(false);

  const [agreeKvkk, setAgreeKvkk] = useState(false);
  const [agreeRiza, setAgreeRiza] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeDpa, setAgreeDpa] = useState(false);

  const [documentFile, setDocumentFile] = useState(null);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [paymentRef, setPaymentRef] = useState("");
  const [paymentOk, setPaymentOk] = useState(false);
  const [cardLoading, setCardLoading] = useState(false);
  const [cardPaymentStarted, setCardPaymentStarted] = useState(false);
  const [paymentSource, setPaymentSource] = useState("");

  // Kayıt ücreti role göre (KDV dahil, tüm dillerde) — backend payment/constants.py
  // PACKAGES ve REGISTER_PACKAGE_BY_ROLE ile aynı tutulmalıdır.
  const packageId = role === "supplier" ? "supplier_register" : "doctor_clinic_register";
  const registerFee = role === "supplier" ? 1000 : 300;

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const setErrorTop = (msg) => {
    setError(msg);
    scrollToTop();
  };

  const validatePaymentStartFields = () => {
    if (!username.trim()) return t("register.validation2.usernameRequired");
    if (!email.trim()) return t("register.validation2.emailRequired");
    return "";
  };

  useEffect(() => {
    if (openPdf) {
      const scrollBarWidth =
        window.innerWidth - document.documentElement.clientWidth;

      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollBarWidth}px`;
      document.documentElement.style.scrollbarGutter = "stable";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
      document.documentElement.style.scrollbarGutter = "";
      document.documentElement.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
      document.documentElement.style.scrollbarGutter = "";
      document.documentElement.style.overflow = "";
    };
  }, [openPdf]);

  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const paidRef = (qs.get("payment_ref") || qs.get("ref") || "").trim();
    const paymentStatus = (qs.get("payment") || "").trim().toLowerCase();
    const reason = (qs.get("reason") || "").trim();

    if (paymentStatus === "success" && paidRef) {
      const draft = loadRegisterDraft();

      if (draft.fullName) setFullName(draft.fullName);
      if (draft.username) setUsername(draft.username);
      if (draft.email) setEmail(draft.email);
      if (draft.role && ["doctor", "clinic", "supplier"].includes(draft.role)) {
        setRole(draft.role);
      }

      setPaymentRef(paidRef);
      setPaymentOk(true);
      setCardPaymentStarted(true);
      setPaymentSource("card");

      saveRegisterPaymentState({
        ref: paidRef,
        ok: true,
        source: "card",
      });

      return;
    }

    if (paymentStatus === "fail") {
      const draft = loadRegisterDraft();

      if (draft.fullName) setFullName(draft.fullName);
      if (draft.username) setUsername(draft.username);
      if (draft.email) setEmail(draft.email);
      if (draft.role && ["doctor", "clinic", "supplier"].includes(draft.role)) {
        setRole(draft.role);
      }

      setPaymentOk(false);
      setPaymentRef("");
      setCardPaymentStarted(false);
      setPaymentSource("");
      clearRegisterPaymentState();

      if (reason) {
        setErrorTop(t("register.paymentBox.cardPaymentFailed"));
      }

      return;
    }

    const savedPayment = loadRegisterPaymentState();

    if (savedPayment?.ok && savedPayment?.ref) {
      const draft = loadRegisterDraft();

      if (draft.fullName) setFullName(draft.fullName);
      if (draft.username) setUsername(draft.username);
      if (draft.email) setEmail(draft.email);
      if (draft.role && ["doctor", "clinic", "supplier"].includes(draft.role)) {
        setRole(draft.role);
      }

      setPaymentRef(savedPayment.ref);
      setPaymentOk(true);
      setPaymentSource(savedPayment.source || "");
      setCardPaymentStarted(savedPayment.source === "card");

      return;
    }

    clearRegisterPaymentState();
    clearRegisterDraft();

    setPaymentOk(false);
    setPaymentRef("");
    setCardPaymentStarted(false);
    setPaymentSource("");
  }, [location.search]);

  const validate = () => {
    if (!username.trim()) return t("register.validation2.usernameRequired");
    if (!email.trim()) return t("register.validation2.emailRequired");
    if (!password) return t("register.validation2.passwordRequired");
    if (!password2) return t("register.validation2.password2Required");

    if (password.length < 6) return t("register.validation.passwordMin");
    if (password !== password2) return t("register.validation.passwordMismatch");

    if (!["doctor", "supplier", "clinic"].includes(role)) {
      return t("register.validation.invalidRole");
    }

    if (!paymentOk) {
      return t("register.validation2.paymentRequired");
    }

    if (!agreeKvkk) return t("register.validation2.mustAgreeKvkk");
    if (!agreeRiza) return t("register.validation2.mustAgreeRiza");
    if (!agreeTerms) return t("register.validation2.mustAgreeTerms");
    if (!agreeDpa) return t("register.validation2.mustAgreeDpa");

    if (role === "supplier" && !documentFile) {
      return t("register.validation2.supplierDocRequired");
    }

    return "";
  };

  const handleCardPayment = async () => {
    if (cardLoading) return;

    const fieldError = validatePaymentStartFields();
    if (fieldError) {
      setErrorTop(fieldError);
      return;
    }

    saveRegisterDraft({
      fullName: fullName.trim(),
      username: username.trim(),
      email: email.trim(),
      role,
    });

    clearRegisterPaymentState();

    setError("");
    setCardLoading(true);

    try {
      const createRes = await axiosInstance.post(
        "/api/payment/orders/register/create/",
        {
          package_id: packageId,
          full_name: fullName.trim() || username.trim(),
          username: username.toLowerCase().trim(),
          email: email.trim(),
        }
      );

      const orderId = createRes?.data?.order_id;

      if (!orderId) {
        throw new Error(t("buyCredits.orderCreateFailed"));
      }

      const startRes = await axiosInstance.post(
        `/api/payment/orders/register/${orderId}/start/`
      );

      const paymentUrl = startRes?.data?.payment_url;

      if (!paymentUrl) {
        throw new Error(t("buyCredits.paymentLinkFailed"));
      }

      setCardPaymentStarted(true);
      window.location.href = paymentUrl;
    } catch (err) {
      const msg =
        err?.response?.data?.detail || t("buyCredits.cardPaymentFailedGeneric");

      setErrorTop(msg);
      setCardPaymentStarted(false);
    } finally {
      setCardLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    const v = validate();
    if (v) {
      setErrorTop(v);
      return;
    }

    setError("");
    setLoading(true);

    try {
      const fd = new FormData();

      fd.append("full_name", fullName.trim());
      fd.append("username", username.toLowerCase().trim());
      fd.append("email", email.trim());
      fd.append("role", role);
      fd.append("password", password);
      fd.append("password2", password2);

      if (paymentRef) {
        fd.append("payment_reference", paymentRef);
      }

      if (documentFile) {
        fd.append("document", documentFile);
      }

      await axiosInstance.post("/api/auth/register/", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      clearRegisterPaymentState();
      clearRegisterDraft();

      navigate("/login?pending=1");
    } catch (err) {
      const data = err?.response?.data;
      const status = err?.response?.status;

      const msg =
        data?.detail ||
        data?.error ||
        (data?.username ? data.username.join(" ") : "") ||
        (data?.email ? data.email.join(" ") : "") ||
        (status === 402
          ? t("register.error2.paymentRequired")
          : status === 400
          ? t("register.error.badRequest")
          : t("register.error.generic"));

      setErrorTop(msg);
    } finally {
      setLoading(false);
    }
  };

  const currentPdfUrl =
    openPdf === "kvkk"
      ? "/contracts/kvkk-aydinlatma.pdf"
      : openPdf === "riza"
      ? "/contracts/kvkk-acik-riza.pdf"
      : openPdf === "dpa"
      ? "/contracts/veri-isleyen-sozlesmesi.pdf"
      : "/contracts/abonelik-sozlesmesi.pdf";

  const absolutePdfUrl = `${window.location.origin}${currentPdfUrl}`;

  const [numPages, setNumPages] = useState(null);
  const [useIframeFallback, setUseIframeFallback] = useState(false);

  useEffect(() => {
    if (!openPdf) return;

    setNumPages(null);
    setUseIframeFallback(false);
  }, [openPdf]);

  const pdfWrapRef = useRef(null);
  const [pageWidth, setPageWidth] = useState(860);

  useEffect(() => {
    if (!openPdf) return;

    const el = pdfWrapRef.current;
    if (!el) return;

    const ro = new ResizeObserver(() => {
      const w = el.clientWidth;
      const next = Math.max(320, Math.min(860, w - 10));
      setPageWidth(next);
    });

    ro.observe(el);

    const w0 = el.clientWidth;
    setPageWidth(Math.max(320, Math.min(860, w0 - 10)));

    return () => ro.disconnect();
  }, [openPdf]);

  return (
    <div className="auth-container ta">
      <div className="auth-box ta">
        <h2 className="auth-title ta">{t("register.title")}</h2>

        {error && <div className="position-alert ta">{error}</div>}

        {paymentOk && paymentRef && (
          <div
            className="position-alert ta"
            style={{ marginBottom: 12, background: "#f5fff7" }}
          >
            {t("register.paymentVerifiedCard")}

            <div style={{ marginTop: 6, fontSize: 12, opacity: 0.85 }}>
              {t("register.referenceLabel")} <b>{paymentRef}</b>
            </div>
          </div>
        )}

        <form className="auth-form ta" onSubmit={handleSubmit}>
          <div className="form-info ta">
            <span className="required">*</span> {t("register.requiredFieldsNote")}
          </div>
          <label className="ta">{t("register.fullNameLabel")}</label>
          <input
            className="ta"
            type="text"
            placeholder={t("register.fullNamePlaceholder")}
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            autoComplete="name"
          />

          <label className="ta">
            {t("register.usernameLabel")} <span className="required">*</span>
          </label>
          <input
            className="ta"
            type="text"
            placeholder={t("register.usernamePlaceholder")}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            required
          />

          <label className="ta">
            {t("register.emailLabel")} <span className="required">*</span>
          </label>
          <input
            className="ta"
            type="email"
            placeholder={t("register.emailPlaceholder")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />

          <label className="ta">{t("register.roleLabel")}</label>
          <select
            className="ta"
            value={role}
            onChange={(e) => setRole(e.target.value)}
          >
            <option value="doctor">{t("register.roles.doctor")}</option>
            <option value="supplier">{t("register.roles.supplier")}</option>
            <option value="clinic">{t("register.roles.clinic")}</option>
          </select>

          <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
            <div
              className="position-alert ta"
              style={{ background: "#f5f9ff", border: "1px solid #dbe7ff" }}
            >
              <b>{t("register.paymentBox.title")}</b>

              <div
                style={{
                  marginTop: 6,
                  color: "#b91c1c",
                  fontWeight: 800,
                  fontSize: 13,
                }}
              >
                {t("register.paymentBox.subscriptionNote")}
              </div>

              <div style={{ marginTop: 8, lineHeight: 1.7 }}>
                <div>
                  <b>{t("register.paymentBox.feeLabel")}</b>{" "}
                  <span style={{ color: "#15803d", fontWeight: 700, fontSize: 18 }}>
                    {t("register.paymentBox.feeAmount", { amount: registerFee })}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
                  {t("register.paymentBox.vatNote", { amount: registerFee })}
                </div>
              </div>
            </div>

            <button
              type="button"
              className="auth-button ta card-pay-btn"
              onClick={handleCardPayment}
              disabled={cardLoading || loading}
              style={{
                background: "linear-gradient(90deg, #111827, #374151)",
                color: "#ffffff",
                opacity: 1,
              }}
            >
              {cardLoading ? t("register.paymentBox.redirecting") : t("register.paymentBox.payButton")}
            </button>

            <p style={{ fontSize: 12, color: "#6b7280", marginTop: 8 }}>
              {t("register.paymentBox.secureNote")}
            </p>
          </div>

          <label className="ta">
            {t("register.passwordLabel")} <span className="required">*</span>
          </label>
          <div className="password-wrapper ta">
            <input
              className="ta"
              type={showPwd1 ? "text" : "password"}
              placeholder={t("register.passwordPlaceholder")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
            />

            <button
              type="button"
              className="toggle-password icon-btn"
              onClick={() => setShowPwd1((s) => !s)}
            >
              {showPwd1 ? <FiEyeOff /> : <FiEye />}
            </button>
          </div>

          <label className="ta">
            {t("register.password2Label")} <span className="required">*</span>
          </label>
          <div className="password-wrapper ta">
            <input
              className="ta"
              type={showPwd2 ? "text" : "password"}
              placeholder={t("register.password2Placeholder")}
              value={password2}
              onChange={(e) => setPassword2(e.target.value)}
              autoComplete="new-password"
              required
            />

            <button
              type="button"
              className="toggle-password icon-btn"
              onClick={() => setShowPwd2((s) => !s)}
            >
              {showPwd2 ? <FiEyeOff /> : <FiEye />}
            </button>
          </div>

          {role === "supplier" && (
            <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
              <label className="ta">
                {t("register.supplierDoc.label")}{" "}
                <b>{t("register.supplierDoc.requiredNote")}</b>
              </label>

              <label
                className="btn-ghost ta"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  border: "1px solid #dbe7ff",
                  borderRadius: 10,
                  padding: "8px 10px",
                  cursor: "pointer",
                }}
              >
                <FiUpload />
                <span>{t("register.document.choose")}</span>

                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  style={{ display: "none" }}
                  onChange={(e) => setDocumentFile(e.target.files?.[0] || null)}
                />
              </label>

              {documentFile && (
                <div
                  className="position-alert ta"
                  style={{ background: "#f5fff7" }}
                >
                  {t("register.supplierDoc.selectedFile")} <b>{documentFile.name}</b>
                </div>
              )}
            </div>
          )}

          <div className="form-info ta">
            <span className="required">*</span> {t("register.requiredAgreementsNote")}
          </div>

          <label className="auth-check-row">
            <input
              type="checkbox"
              checked={agreeDpa}
              onChange={() => {
                if (!opened.dpa) {
                  setOpened((o) => ({ ...o, dpa: true }));
                  setOpenPdf("dpa");
                  return;
                }

                setAgreeDpa((v) => !v);
              }}
            />

            <span>
              <button
                type="button"
                className="link-btn"
                onClick={() => {
                  setOpened((o) => ({ ...o, dpa: true }));
                  setOpenPdf("dpa");
                }}
              >
                {t("register.agree2.dpaLink")}
              </button>{" "}
              {t("register.agree2.dpaSuffix")} <span className="required">*</span>
          </span>
          </label>

          <label className="auth-check-row">
            <input
              type="checkbox"
              checked={agreeKvkk}
              onChange={() => {
                if (!opened.kvkk) {
                  setOpened((o) => ({ ...o, kvkk: true }));
                  setOpenPdf("kvkk");
                  return;
                }

                setAgreeKvkk((v) => !v);
              }}
            />

            <span>
              <button
                type="button"
                className="link-btn"
                onClick={() => {
                  setOpened((o) => ({ ...o, kvkk: true }));
                  setOpenPdf("kvkk");
                }}
              >
                {t("register.agree2.kvkkLink")}
              </button>{" "}
              {t("register.agree2.kvkkSuffix")}<span className="required">*</span>
            </span>
          </label>

          <label className="auth-check-row">
            <input
              type="checkbox"
              checked={agreeRiza}
              onChange={() => {
                if (!opened.riza) {
                  setOpened((o) => ({ ...o, riza: true }));
                  setOpenPdf("riza");
                  return;
                }

                setAgreeRiza((v) => !v);
              }}
            />

            <span>
              <button
                type="button"
                className="link-btn"
                onClick={() => {
                  setOpened((o) => ({ ...o, riza: true }));
                  setOpenPdf("riza");
                }}
              >
                {t("register.agree2.rizaLink")}
              </button>{" "}
              {t("register.agree2.rizaSuffix")}<span className="required">*</span>
            </span>
          </label>

          <label className="auth-check-row">
            <input
              type="checkbox"
              checked={agreeTerms}
              onChange={() => {
                if (!opened.terms) {
                  setOpened((o) => ({ ...o, terms: true }));
                  setOpenPdf("terms");
                  return;
                }

                setAgreeTerms((v) => !v);
              }}
            />

            <span>
              <button
                type="button"
                className="link-btn"
                onClick={() => {
                  setOpened((o) => ({ ...o, terms: true }));
                  setOpenPdf("terms");
                }}
              >
                {t("register.agree2.termsLink")}
              </button>{" "}
              {t("register.agree2.termsSuffix")}<span className="required">*</span>
            </span>
          </label>

          <button
            type="submit"
            className="auth-button ta"
            disabled={
              loading ||
              cardLoading ||
              !paymentOk ||
              !agreeKvkk ||
              !agreeRiza ||
              !agreeTerms ||
              !agreeDpa ||
              (role === "supplier" && !documentFile)
            }
          >
            {loading ? t("register.submitting") : t("register.submit")}
          </button>
        </form>

        {openPdf &&
          createPortal(
            <div
              className="pdf-modal"
              onClick={() => setOpenPdf(null)}
              role="dialog"
              aria-modal="true"
            >
              <div className="pdf-content" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  className="close-btn"
                  onClick={() => setOpenPdf(null)}
                  aria-label={t("register.close")}
                >
                  ✕
                </button>

                <div className="pdf-scroll" ref={pdfWrapRef}>
                  {!useIframeFallback ? (
                    <Document
                      file={absolutePdfUrl}
                      onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                      loading={
                        <div className="pdf-loading">{t("register.pdf.preparing")}</div>
                      }
                      onLoadError={(e) => {
                        console.error("react-pdf load error:", e);
                        setUseIframeFallback(true);
                      }}
                      error={
                        <div className="pdf-loading">{t("register.pdf.loadFailed")}</div>
                      }
                    >
                      {numPages
                        ? Array.from({ length: numPages }, (_, i) => (
                            <Page
                              key={`p_${i + 1}`}
                              pageNumber={i + 1}
                              width={pageWidth}
                              renderTextLayer={false}
                              renderAnnotationLayer={false}
                            />
                          ))
                        : null}
                    </Document>
                  ) : (
                    <div className="pdf-viewer" style={{ height: "100%" }}>
                      <iframe
                        title={t("register.pdf.title")}
                        src={`${currentPdfUrl}#view=FitH&scrollbar=1&toolbar=1&navpanes=0`}
                        style={{
                          width: "100%",
                          height: "100%",
                          border: "none",
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>,
            document.body
          )}

        <p className="auth-link ta" style={{ marginTop: 14 }}>
          {t("register.haveAccount")}{" "}
          <Link to="/login">{t("register.login")}</Link>
        </p>
      </div>
    </div>
  );
}